import { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Check, ArrowRight, Monitor, Users, Calculator, Package, Receipt, TrendingUp, Utensils, Briefcase, Star, Building2, Layers, ChevronRight, Play, Pause, BarChart3, Globe, Shield, Calendar, FileText, Zap, Factory, Bell, Wrench, ChevronDown, Lock, Clock, Award, MessageSquare, Heart, MapPin, Send, Sparkles, Menu, X, Paintbrush, Rocket, Moon, Download, Smartphone, Printer, WifiOff } from 'lucide-react';
import { AnimatedCounter, HowItWorks, YourDayTimeline, FeaturePlayground, FloatingParticles } from '../components/LandingAnimations';
import ProductTourModal from '../components/ProductTourModal';
import InstallAppBanner from '../components/marketing/InstallAppBanner';
import ApkBuildBadge from '../components/marketing/ApkBuildBadge';

import { Reveal, BrowserFrame } from './landing/primitives';
import { ProductShowcase } from './landing/ProductShowcase';
import { heading, headingWhite, INDUSTRY_OPTIONS, MODULE_PILLS, INDUSTRIES, PRICING, TESTIMONIALS, FAQS, INTEGRATIONS, SOCIAL_LINKS, getDailyPosts, GALLERY, CUSTOMER_LOGOS } from './landing/landingData';
import { ROICalculator, ComparisonTable } from './landing/LandingExtras';

export default function LandingPage() {
  const navigate = useNavigate();
  const [tourOpen, setTourOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const [socialProof, setSocialProof] = useState(null);
  const [tickerItem, setTickerItem] = useState(null);
  const [tickerVisible, setTickerVisible] = useState(false);
  const [tickerDismissed, setTickerDismissed] = useState(false);
  const [exitPopup, setExitPopup] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: '', email: '', phone: '', industry: '', message: '' });
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  // Pre-rendered industry options. NOTE: the preview environment's
  // visual-editor instrumentation wraps every JSX `.map()` output in a
  // <span style="display:contents"> for change tracking, which then
  // emits a noisy "<span> cannot be a child of <select>" hydration
  // warning. The warning is preview-only — production builds strip
  // the instrumentation and the warning vanishes.
  const industryOptions = INDUSTRY_OPTIONS.map(i => (
    <option key={i} value={i}>{i}</option>
  ));
  const [leadError, setLeadError] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dailyPosts = getDailyPosts();

  // Fetch social proof stats
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/social-proof/stats`)
      .then(r => r.json())
      .then(d => setSocialProof(d))
      .catch(() => {});
  }, []);

  // Visitor Activity Ticker
  useEffect(() => {
    // iter751 — Disabled per user request: the recurring "X just signed up"
    // ticker (re-popped every 8s) was intrusive. Flip SHOW_ACTIVITY_TICKER
    // back to true to re-enable. The static social-proof stats banner below
    // stays for credibility (it's inline, not a popup).
    const SHOW_ACTIVITY_TICKER = false;
    if (!SHOW_ACTIVITY_TICKER || tickerDismissed) return;
    let activities = [];
    let index = 0;
    let interval;

    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/social-proof/activity`)
      .then(r => r.json())
      .then(d => {
        activities = d.activities || [];
        if (activities.length === 0) return;

        const showNext = () => {
          if (tickerDismissed) return;
          setTickerVisible(false);
          setTimeout(() => {
            setTickerItem(activities[index % activities.length]);
            setTickerVisible(true);
            index++;
          }, 400);
          // Hide after 4.5s
          setTimeout(() => setTickerVisible(false), 5000);
        };

        // Show first one after 3s delay
        setTimeout(showNext, 3000);
        // Then cycle every 8s
        interval = setInterval(showNext, 8000);
      })
      .catch(() => {});

    return () => clearInterval(interval);
  }, [tickerDismissed]);

  // Exit-Intent Detection
  useEffect(() => {
    // iter751 — Disabled per user request (too many popups). The
    // exit-intent overlay no longer appears. Flip SHOW_EXIT_INTENT back
    // to true to re-enable it (already capped to once per session).
    const SHOW_EXIT_INTENT = false;
    if (!SHOW_EXIT_INTENT) return;
    if (sessionStorage.getItem('smarto_exit_shown')) return;
    let triggered = false;
    const handleMouseLeave = (e) => {
      if (triggered) return;
      if (e.clientY <= 5 && e.relatedTarget === null) {
        triggered = true;
        sessionStorage.setItem('smarto_exit_shown', '1');
        setExitPopup(true);
      }
    };
    // Only activate after 5s on page (avoid immediate triggers)
    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 5000);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  // Lead form submit
  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setLeadError('');
    if (!leadForm.name.trim() || !leadForm.email.trim()) {
      setLeadError('Please fill in your name and email.');
      return;
    }
    setLeadSubmitting(true);
    try {
      const sessionId = sessionStorage.getItem('smarto_session_id') || sessionStorage.getItem('smarto_demo_session') || '';
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...leadForm, session_id: sessionId }),
      });
      const data = await res.json();
      if (data.error) {
        setLeadError(data.error);
      } else {
        setLeadSubmitted(true);
      }
    } catch {
      setLeadError('Something went wrong. Please try again.');
    }
    setLeadSubmitting(false);
  };

  // Auto-open tour if ?demo=true is in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('demo') === 'true') {
      setTourOpen(true);
      // Clean the URL without reload
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Track landing page visit (once per browser session)
  useEffect(() => {
    const key = 'smarto_visit_tracked';
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    const sid = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem('smarto_session_id', sid);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/demo-analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event_type: 'page_visit', session_id: sid, source: 'direct' }),
    }).catch(() => {});
  }, []);

  // Show sticky CTA after scrolling past hero
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCta(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="landing-page">

      {/* ═══════ NAV ═══════ */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-2xl bg-white/80 border-b border-gray-100/50 shadow-[0_1px_3px_rgb(0,0,0,0.03)]" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate('/')} data-testid="nav-logo-home">
            <img src="/smarto-icon.png" alt="Smarto — back to home" className="h-7 w-7 object-contain" />
            <span className="text-lg font-extrabold tracking-wider" style={heading}>SMARTO</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            {['Features', 'Preview', 'Industries', 'Pricing'].map(l => (
              <a key={l} href={`#${l.toLowerCase()}`} className="text-[13px] font-medium text-[#86868B] hover:text-[#1D1D1F] transition-colors duration-200">{l}</a>
            ))}
            <a href="/api/apk/download" download className="flex items-center gap-1.5 text-[13px] font-semibold text-[#047857] hover:text-[#065F46] transition-colors duration-200" data-testid="nav-download-app">
              <Smartphone className="w-3.5 h-3.5" /> Download App
            </a>
            <a href="/downloads/SmartoERP-Setup-latest.exe" download className="flex items-center gap-1.5 text-[13px] font-semibold text-[#0369A1] hover:text-[#0C4A6E] transition-colors duration-200" data-testid="nav-download-windows" title="Native Windows POS · USB thermal printer support">
              <Monitor className="w-3.5 h-3.5" /> Windows
            </a>
            <button onClick={() => navigate('/login')} className="text-[13px] font-semibold text-[#1D1D1F] hover:text-[#065F46] transition-colors duration-200" data-testid="nav-login">Sign In</button>
            <button onClick={() => navigate('/register')} className="bg-[#047857] hover:bg-[#065F46] text-white px-5 py-2 rounded-full text-[13px] font-bold transition-all active:scale-[0.98] shadow-md shadow-[#047857]/25 hover:shadow-lg hover:shadow-[#047857]/30" data-testid="nav-get-started">Start Free Trial</button>
          </div>
          <button className="md:hidden text-[#1D1D1F] p-1" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-100 bg-white/95 backdrop-blur-xl px-6 py-4 space-y-3" data-testid="mobile-nav-menu">
            {['Features', 'Preview', 'Industries', 'Pricing'].map(l => (
              <a key={l} href={`#${l.toLowerCase()}`} onClick={() => setMobileMenuOpen(false)}
                className="block text-[14px] font-medium text-[#86868B] hover:text-[#1D1D1F] py-2 transition-colors">{l}</a>
            ))}
            <div className="pt-2 border-t border-gray-100 flex flex-col gap-2">
              <a href="/api/apk/download" download onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 text-[14px] font-semibold text-[#047857] py-2" data-testid="mobile-nav-download-app">
                <Smartphone className="w-4 h-4" /> Download Android App
              </a>
              <a href="/downloads/SmartoERP-Setup-latest.exe" download onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 text-[14px] font-semibold text-[#0369A1] py-2" data-testid="mobile-nav-download-windows">
                <Monitor className="w-4 h-4" /> Download for Windows
              </a>
              <button onClick={() => { setMobileMenuOpen(false); navigate('/login'); }}
                className="text-[14px] font-semibold text-[#1D1D1F] py-2 text-left" data-testid="mobile-nav-login">Sign In</button>
              <button onClick={() => { setMobileMenuOpen(false); navigate('/register'); }}
                className="bg-[#1D1D1F] text-white px-5 py-2.5 rounded-full text-[13px] font-bold w-full" data-testid="mobile-nav-register">Start Free Trial</button>
            </div>
          </div>
        )}
      </nav>

      {/* ═══════ HERO ═══════ */}
      <section className="pt-32 pb-4 px-6 md:px-12 relative overflow-hidden">
        <FloatingParticles />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <Reveal>
            <div className="inline-flex items-center gap-2 bg-[#F5F5F7] rounded-full px-4 py-1.5 mb-6">
              <Globe className="w-3.5 h-3.5 text-[#047857]" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#1D1D1F]">One App. Any Country. Every Branch.</span>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tighter leading-[1.05]" style={heading}>
              One Platform For<br />
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#065F46] to-[#10B981]">Your Entire Business.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-lg text-[#86868B] mt-6 max-w-2xl mx-auto leading-relaxed">
              Whether you run one shop or branches across multiple countries — manage everything from a single dashboard.
            </p>
          </Reveal>
          <Reveal delay={0.22}>
            <div className="flex flex-wrap items-center justify-center gap-2.5 mt-8">
              {MODULE_PILLS.map((m, i) => (
                <motion.div key={m.label}
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.4 + i * 0.07 }}
                  onClick={() => navigate(m.link)}
                  className="group flex items-center gap-2 px-4 py-2 rounded-full border border-gray-200 bg-white hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer"
                  data-testid={`hero-pill-${m.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${m.color} flex items-center justify-center`}>
                    <m.icon className="w-3 h-3 text-white" />
                  </div>
                  <span className="text-[13px] font-semibold text-[#1D1D1F]">{m.label}</span>
                </motion.div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
              <button onClick={() => navigate('/register')} className="bg-[#047857] hover:bg-[#065F46] text-white px-8 py-3.5 rounded-full text-[15px] font-semibold transition-all flex items-center gap-2.5 active:scale-[0.98] shadow-xl shadow-[#047857]/30 hover:shadow-2xl hover:shadow-[#047857]/40 hover:scale-[1.02]" data-testid="hero-start-btn">
                Start Free Trial <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={() => setTourOpen(true)} className="bg-white hover:bg-gray-50 text-[#1D1D1F] border-2 border-[#0F172A] px-8 py-3.5 rounded-full text-[15px] font-semibold transition-all flex items-center gap-2.5 active:scale-[0.98] shadow-sm" data-testid="hero-live-demo-btn">
                <Play className="w-4 h-4" /> Live Demo
              </button>
            </div>
          </Reveal>
          <Reveal delay={0.35}>
            <div className="flex items-center justify-center gap-6 mt-8 text-[13px] text-[#86868B]">
              {['No credit card required', '14-day free trial', 'Multi-country support'].map(t => (
                <span key={t} className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-success" />{t}</span>
              ))}
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.4}>
          <ProductShowcase />
        </Reveal>
      </section>

      {/* ═══════ TRUST & COMPLIANCE BAND (premium dark rhythm) ═══════ */}
      <section className="relative overflow-hidden bg-[#0B1120] py-14 md:py-16" data-testid="trust-compliance-band">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '26px 26px' }} aria-hidden />
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[42rem] h-[42rem] bg-[#047857]/15 blur-[120px] rounded-full" aria-hidden />
        <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
          <Reveal>
            <div className="text-center mb-10">
              <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#D97706]">Built For Compliance</p>
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-white mt-3" style={headingWhite}>
                Tax-ready in every market you operate.
              </h2>
              <p className="text-[15px] text-[#94A3B8] mt-3 max-w-2xl mx-auto leading-relaxed">
                From Saudi ZATCA e-invoicing to multi-country VAT & payroll law — Smarto keeps every branch compliant, automatically.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3.5" data-testid="compliance-badges">
              {[
                { icon: Receipt, label: 'ZATCA Phase-2', sub: 'E-Invoicing live' },
                { icon: Globe, label: '31+ Country', sub: 'Tax & labor law' },
                { icon: Calculator, label: 'VAT & GOSI', sub: 'Auto-calculated' },
                { icon: Layers, label: 'Multi-Currency', sub: '18 currencies' },
                { icon: Shield, label: 'AES-256', sub: 'Encrypted at rest' },
                { icon: TrendingUp, label: '99.9% Uptime', sub: 'Enterprise SLA' },
              ].map((b, i) => (
                <motion.div key={b.label}
                  initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="group rounded-2xl bg-[#1E293B]/60 border border-[#334155] px-4 py-4 backdrop-blur-sm hover:border-[#047857] hover:bg-[#1E293B] transition-all duration-300"
                  data-testid={`compliance-badge-${i}`}>
                  <div className="w-9 h-9 rounded-xl bg-[#047857]/15 flex items-center justify-center mb-2.5 group-hover:bg-[#047857]/25 transition-colors">
                    <b.icon className="w-[18px] h-[18px] text-[#34D399]" strokeWidth={1.75} />
                  </div>
                  <p className="text-[13.5px] font-semibold text-white leading-tight">{b.label}</p>
                  <p className="text-[11px] text-[#94A3B8] mt-0.5">{b.sub}</p>
                </motion.div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════ INSTALL APP BANNER (iter740) ═══════ */}
      <Reveal>
        <InstallAppBanner />
      </Reveal>

      {/* ═══════ SOCIAL PROOF BANNER ═══════ */}
      {socialProof && (
        <section className="py-4 bg-[#0A0A0A] relative overflow-hidden" data-testid="social-proof-banner">
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
          <div className="max-w-6xl mx-auto px-6 relative z-10">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
              {[
                { val: socialProof.total_users || 0, label: 'Businesses Active', suffix: '+', icon: Users },
                { val: socialProof.total_branches || 0, label: 'Branches Connected', suffix: '+', icon: Building2 },
                { val: socialProof.demo_completions || 0, label: 'Demo Tours Completed', suffix: '', icon: Play },
                { val: socialProof.countries_active || 3, label: 'Countries', suffix: '+', icon: Globe },
              ].map((s, i) => (
                <motion.div key={s.label}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.08 }}
                  className="flex items-center gap-2.5 px-4 py-1.5">
                  <s.icon className="w-4 h-4 text-[#D97706]" />
                  <span className="text-white font-bold text-[15px]" style={headingWhite}>{s.val}{s.suffix}</span>
                  <span className="text-[11px] text-gray-400 font-medium">{s.label}</span>
                </motion.div>
              ))}
              {socialProof.week_signups > 0 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 }}
                  className="flex items-center gap-2 bg-success/10 border border-success/20 rounded-full px-4 py-1.5">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-[11px] text-emerald-300 font-semibold">{socialProof.week_signups} new this week</span>
                </motion.div>
              )}
            </motion.div>
          </div>
        </section>
      )}

      {/* ═══════ CUSTOMER LOGOS ═══════ */}
      <section className="py-12 md:py-16 border-b border-gray-100" data-testid="customer-logos-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-center text-[11px] font-bold uppercase tracking-[0.2em] text-[#86868B] mb-8">
              Trusted by growing businesses
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6 md:gap-x-16">
              {CUSTOMER_LOGOS.map((c) => (
                <div key={c.name}
                  className="flex items-center gap-2 grayscale opacity-50 hover:opacity-100 hover:grayscale-0 transition-all duration-300"
                  data-testid={`customer-logo-${c.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  <c.icon className="w-5 h-5 text-[#1D1D1F]" />
                  <span className="text-[17px] font-extrabold tracking-tight text-[#1D1D1F]" style={heading}>{c.name}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════ STATS ═══════ */}
      <section className="py-16 md:py-20">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center px-6">
          {[
            { val: 11, suffix: '+', label: 'Modules' },
            { val: 31, suffix: '+', label: 'Country Laws' },
            { val: 100, suffix: '+', label: 'Features' },
            { val: 99, suffix: '.9%', label: 'Uptime' },
          ].map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <p className="text-4xl md:text-5xl font-semibold tracking-tight" style={heading}>
                <AnimatedCounter value={String(s.val)} suffix={s.suffix} duration={1.5} />
              </p>
              <p className="text-[13px] text-[#86868B] mt-2 font-medium">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ═══════ HOW IT WORKS ═══════ */}
      <section className="py-24 md:py-32 bg-[#FAFAFA]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#047857] mb-3">How It Works</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight" style={heading}>
                Up And Running In Minutes.
              </h2>
              <p className="text-lg text-[#86868B] mt-4 max-w-xl mx-auto">Four simple steps to transform how you run your business.</p>
            </div>
          </Reveal>
          <HowItWorks />
        </div>
      </section>

      {/* ═══════ POS SPOTLIGHT ═══════ */}
      <section id="features" className="py-24 md:py-32 bg-[#F5F5F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <Reveal>
              <div>
                <div className="inline-flex items-center gap-2 bg-white rounded-full px-3 py-1 mb-5">
                  <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[#047857] to-red-500 flex items-center justify-center">
                    <Monitor className="w-2.5 h-2.5 text-white" />
                  </div>
                  <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#86868B]">Point of Sale</span>
                </div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.1]" style={heading}>
                  A POS Built For<br />
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#065F46] to-[#10B981]">Every Industry.</span>
                </h2>
                <p className="text-[15px] text-[#86868B] leading-relaxed mt-6">
                  Restaurants, retail, tailoring, bakeries, electronics, salons — one POS adapts to all. With kitchen display, table management, barcode scanning, and receipt printing built in.
                </p>
                <div className="grid grid-cols-2 gap-3 mt-8">
                  {[
                    { icon: Utensils, text: 'Restaurant & cafe mode' },
                    { icon: Package, text: 'Retail & barcode scanning' },
                    { icon: Briefcase, text: 'Tailoring order tracking' },
                    { icon: Layers, text: 'Bakery production' },
                    { icon: Zap, text: 'Offline mode support' },
                    { icon: Globe, text: 'Multi-branch sync' },
                  ].map(f => (
                    <div key={f.text} className="flex items-center gap-2.5 text-[13px] text-[#1D1D1F] font-medium">
                      <f.icon className="w-4 h-4 text-[#047857] shrink-0" /> {f.text}
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <BrowserFrame src="/demo/pos.jpg" alt="Smarto POS" route="pos" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════ HR & EMPLOYEE PORTAL ═══════ */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <Reveal className="order-2 lg:order-1">
              <div className="space-y-5">
                <BrowserFrame src="/demo/employees.jpg" alt="Employee Management" route="employees" />
                <div className="grid grid-cols-2 gap-5">
                  <BrowserFrame src="/demo/payroll.jpg" alt="Payroll" route="payroll" />
                  <BrowserFrame src="/demo/schedule.jpg" alt="Schedule" route="schedule" />
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="order-1 lg:order-2">
              <div>
                <div className="inline-flex items-center gap-2 bg-[#F5F5F7] rounded-full px-3 py-1 mb-5">
                  <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                    <Users className="w-2.5 h-2.5 text-white" />
                  </div>
                  <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#86868B]">HR & Payroll</span>
                </div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.1]" style={heading}>
                  Your People,<br />
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-indigo-600">Fully Managed.</span>
                </h2>
                <p className="text-[15px] text-[#86868B] leading-relaxed mt-6">
                  From hiring to payroll — manage the complete employee lifecycle across 31+ countries. Attendance tracking, leave management, country-aware payroll deductions, and an employee self-service portal.
                </p>
                <ul className="mt-8 space-y-3">
                  {['Employee profiles & contracts', 'Payroll for 31+ countries (GCC, Asia, EU)', 'Attendance & leave management', 'Shift scheduling & calendar', 'Country-aware labor law compliance', 'Employee self-service portal'].map(item => (
                    <li key={item} className="flex items-center gap-3 text-[13px] text-[#1D1D1F] font-medium">
                      <Check className="w-4 h-4 text-info shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════ ACCOUNTING & ANALYTICS ═══════ */}
      <section className="py-24 md:py-32 bg-[#F5F5F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <Reveal>
              <div>
                <div className="inline-flex items-center gap-2 bg-white rounded-full px-3 py-1 mb-5">
                  <div className="w-5 h-5 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                    <Calculator className="w-2.5 h-2.5 text-white" />
                  </div>
                  <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#86868B]">Accounting & Analytics</span>
                </div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.1]" style={heading}>
                  Numbers That<br />
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-500 to-teal-600">Make Sense.</span>
                </h2>
                <p className="text-[15px] text-[#86868B] leading-relaxed mt-6">
                  Full double-entry accounting with AI-powered analytics. Balance sheets, P&L, bank reconciliation, VAT reports, and real-time financial dashboards — all automated.
                </p>
                <ul className="mt-8 space-y-3">
                  {['Chart of accounts & journal entries', 'Balance sheet & profit/loss', 'Bank reconciliation', 'AI sales forecasting', 'Expense anomaly detection', 'Automated report scheduling'].map(item => (
                    <li key={item} className="flex items-center gap-3 text-[13px] text-[#1D1D1F] font-medium">
                      <Check className="w-4 h-4 text-success shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="space-y-5">
                <BrowserFrame src="/demo/finance.jpg" alt="Financial Dashboard" route="financial-dashboard" />
                <BrowserFrame src="/demo/analytics.jpg" alt="Analytics" route="analytics" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════ YOUR DAY WITH SMARTO ═══════ */}
      <section className="py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#047857] mb-3">A Day In Your Life</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight" style={heading}>
                Your Day With Smarto.
              </h2>
              <p className="text-lg text-[#86868B] mt-4 max-w-xl mx-auto">See how Smarto keeps you in control from morning to evening.</p>
            </div>
          </Reveal>
          <YourDayTimeline />
        </div>
      </section>

      {/* ═══════ INTERACTIVE FEATURE PLAYGROUND ═══════ */}
      <section className="py-24 md:py-32 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#047857] mb-3">Interactive</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight" style={heading}>
                Experience It Live.
              </h2>
              <p className="text-lg text-[#86868B] mt-4 max-w-xl mx-auto">Watch real features come alive — this is what your team will use every day.</p>
            </div>
          </Reveal>
          <FeaturePlayground />
        </div>
      </section>

      {/* ═══════ PREVIEW GALLERY ═══════ */}
      <section id="preview" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#047857] mb-3">New</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight" style={heading}>
                What's New In Smarto
              </h2>
              <p className="text-lg text-[#86868B] mt-4 max-w-xl mx-auto">Latest features built to help you scale.</p>
            </div>
          </Reveal>

          {/* New Features Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-16">
            {[
              { icon: Rocket, color: 'from-[#047857] to-[#10B981]', title: 'Onboarding Wizard', desc: '6-step guided setup: company profile, branding, branch creation, currency & tax, first product, and team members. Live in under 5 minutes.', tag: 'New' },
              { icon: Paintbrush, color: 'from-pink-500 to-rose-600', title: 'White-Label Theming', desc: 'Custom branding for every tenant. Your logo, colors, and app name on the login page, sidebar, and all PDF exports. Full brand control.', tag: 'New' },
              { icon: Moon, color: 'from-slate-700 to-slate-900', title: 'Dark Mode', desc: 'System-wide dark theme that works across all 21+ pages. Easy toggle in the sidebar — gentle on the eyes for late-night work sessions.', tag: 'New' },
            ].map((f, i) => (
              <Reveal key={f.title} delay={i * 0.08}>
                <div className="rounded-2xl p-7 bg-white border border-gray-100 hover:shadow-[0_20px_60px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-500 h-full" data-testid={`new-feature-${i}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center shadow-sm`}>
                      <f.icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#047857] bg-[#ECFDF5] px-2 py-0.5 rounded-full">{f.tag}</span>
                  </div>
                  <h3 className="text-[17px] font-semibold tracking-tight mb-2" style={heading}>{f.title}</h3>
                  <p className="text-[14px] text-[#86868B] leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* More Features Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-16">
            {[
              { icon: FileText, color: 'from-emerald-500 to-teal-600', title: 'Branded PDF Exports', desc: 'Quotations, purchase orders, and invoices export with your company logo, colors, address, and VAT — professional documents in one click.', tag: 'Improved' },
              { icon: Globe, color: 'from-blue-500 to-indigo-600', title: 'Multi-Currency', desc: '18 currencies supported with live exchange rates, automatic conversion, and per-tenant primary currency configuration.', tag: 'Built-In' },
              { icon: Sparkles, color: 'from-[#10B981] to-[#065F46]', title: 'Interactive Page Tours', desc: '21 pages with guided walkthroughs. "Take a Tour" button teaches your team every feature with spotlight highlights and step-by-step descriptions.', tag: 'New' },
            ].map((f, i) => (
              <Reveal key={f.title} delay={i * 0.08}>
                <div className="rounded-2xl p-7 bg-white border border-gray-100 hover:shadow-[0_20px_60px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-500 h-full" data-testid={`more-feature-${i}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center shadow-sm`}>
                      <f.icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#047857] bg-[#ECFDF5] px-2 py-0.5 rounded-full">{f.tag}</span>
                  </div>
                  <h3 className="text-[17px] font-semibold tracking-tight mb-2" style={heading}>{f.title}</h3>
                  <p className="text-[14px] text-[#86868B] leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Factory Spotlight */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-20">
            <Reveal>
              <div>
                <div className="inline-flex items-center gap-2 bg-[#F5F5F7] rounded-full px-3 py-1 mb-5">
                  <div className="w-5 h-5 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                    <Factory className="w-2.5 h-2.5 text-white" />
                  </div>
                  <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#86868B]">Factory Module</span>
                </div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.1]" style={heading}>
                  Production Lines,<br />
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-500 to-blue-600">Fully Controlled.</span>
                </h2>
                <p className="text-[15px] text-[#86868B] leading-relaxed mt-6">
                  Manage your entire factory from raw materials to quality control. Bill of materials with cost breakdowns, machine scheduling, production analytics, and defect tracking — built for modern manufacturing.
                </p>
                <div className="grid grid-cols-2 gap-3 mt-8">
                  {[
                    { icon: FileText, text: 'Production order workflow' },
                    { icon: Layers, text: 'Bill of materials & costing' },
                    { icon: Package, text: 'Raw material inventory' },
                    { icon: Wrench, text: 'Machine & equipment mgmt' },
                    { icon: Shield, text: 'Quality control checklists' },
                    { icon: BarChart3, text: 'Yield & defect analytics' },
                  ].map(f => (
                    <div key={f.text} className="flex items-center gap-2.5 text-[13px] text-[#1D1D1F] font-medium">
                      <f.icon className="w-4 h-4 text-cyan-500 shrink-0" /> {f.text}
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-cyan-50 to-blue-50 border border-cyan-100/50 p-8">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Production Orders', val: 'Track & manage', color: 'bg-info' },
                    { label: 'Bill of Materials', val: 'Cost breakdowns', color: 'bg-violet-500' },
                    { label: 'Raw Materials', val: 'Stock alerts', color: 'bg-warning' },
                    { label: 'Machines', val: 'Maintenance logs', color: 'bg-cyan-500' },
                    { label: 'Quality Control', val: 'Batch inspections', color: 'bg-success' },
                    { label: 'Analytics', val: 'Yield & defects', color: 'bg-purple-500' },
                  ].map(item => (
                    <div key={item.label} className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-white/60 shadow-sm">
                      <div className={`w-2 h-2 rounded-full ${item.color} mb-2`} />
                      <p className="text-[13px] font-semibold text-[#1D1D1F]">{item.label}</p>
                      <p className="text-[11px] text-[#86868B] mt-0.5">{item.val}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          {/* Original Gallery */}
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#047857] mb-3">See More</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight" style={heading}>
                Every Screen, Designed<br className="hidden md:block" /> To Get Work Done.
              </h2>
              <p className="text-lg text-[#86868B] mt-4 max-w-xl mx-auto">Explore the full platform — from invoicing to inventory, platforms to scheduling.</p>
            </div>
          </Reveal>

          {/* Featured row — 2 large cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {GALLERY.slice(0, 2).map((item, i) => (
              <Reveal key={item.label} delay={i * 0.08}>
                <div className="group" data-testid={`gallery-${item.route}`}>
                  <div className="rounded-2xl overflow-hidden border border-gray-200/80 bg-gradient-to-b from-[#FAFAFA] to-[#F0F0F2] shadow-sm group-hover:shadow-[0_20px_60px_rgb(0,0,0,0.10)] group-hover:-translate-y-1.5 transition-all duration-500">
                    <div className="px-6 pt-6 pb-4 flex items-center justify-between">
                      <div>
                        <p className="text-[16px] font-semibold text-[#1D1D1F]" style={heading}>{item.label}</p>
                        <p className="text-[12px] text-[#86868B] mt-0.5">{item.desc}</p>
                      </div>
                      <div className="w-9 h-9 rounded-full bg-white shadow-sm border border-gray-100 flex items-center justify-center">
                        <item.icon className="w-4 h-4 text-[#047857]" />
                      </div>
                    </div>
                    <div className="px-4 pb-4">
                      <div className="rounded-xl overflow-hidden border border-gray-200/60 shadow-inner">
                        <img src={item.src} alt={item.label} className="w-full" loading="lazy" />
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Bottom row — 4 compact cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {GALLERY.slice(2).map((item, i) => (
              <Reveal key={item.label} delay={i * 0.06}>
                <div className="group" data-testid={`gallery-${item.route}`}>
                  <div className="rounded-xl overflow-hidden border border-gray-200/80 bg-gradient-to-b from-[#FAFAFA] to-[#F0F0F2] shadow-sm group-hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)] group-hover:-translate-y-1 transition-all duration-500">
                    <div className="px-3 pt-3 pb-2 flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-white shadow-sm border border-gray-100 flex items-center justify-center">
                        <item.icon className="w-3 h-3 text-[#047857]" />
                      </div>
                      <p className="text-[13px] font-semibold text-[#1D1D1F]">{item.label}</p>
                    </div>
                    <div className="px-2.5 pb-2.5">
                      <div className="rounded-lg overflow-hidden border border-gray-200">
                        <img src={item.src} alt={item.label} className="w-full" loading="lazy" />
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ NATIVE ANDROID APP ═══════ */}
      <section className="py-24 md:py-32" data-testid="android-app-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="rounded-[2.5rem] md:rounded-[3rem] bg-[#F5F5F7] overflow-hidden border border-black/5 px-7 py-12 md:p-16">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                {/* Left — copy */}
                <div>
                  <div className="inline-flex items-center gap-2 bg-[#047857]/10 rounded-full px-3.5 py-1.5 mb-5">
                    <Smartphone className="w-3.5 h-3.5 text-[#047857]" />
                    <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#047857]">Native Android App</span>
                  </div>
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.08]" style={heading}>
                    Your entire business,<br />
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#047857] to-[#10B981]">in your pocket.</span>
                  </h2>
                  <p className="text-[15px] md:text-[16px] text-[#86868B] leading-relaxed mt-5 max-w-lg">
                    Smarto ships as a real Android app — not just a website. Run your POS even when the internet drops, print straight to thermal, Bluetooth, USB &amp; network receipt printers, and manage every branch from any Android tablet or phone.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3.5 mt-8">
                    {[
                      { icon: WifiOff, text: 'Offline-capable POS & auto-sync' },
                      { icon: Printer, text: 'Thermal, Bluetooth, USB & network printing' },
                      { icon: Download, text: 'One-tap install — auto-updates' },
                      { icon: Smartphone, text: 'Runs on any Android tablet or phone' },
                    ].map(f => (
                      <div key={f.text} className="flex items-center gap-3 text-[13.5px] text-[#1D1D1F] font-medium">
                        <span className="w-8 h-8 rounded-xl bg-white shadow-sm border border-gray-100 flex items-center justify-center shrink-0">
                          <f.icon className="w-4 h-4 text-[#047857]" />
                        </span>
                        {f.text}
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap items-center gap-4 mt-9">
                    <a href="/api/apk/download" download
                       className="inline-flex items-center gap-2.5 bg-[#047857] hover:bg-[#065F46] text-white px-7 py-3.5 rounded-full text-[15px] font-semibold transition-all shadow-xl shadow-[#047857]/25 hover:shadow-2xl hover:scale-[1.03] active:scale-[0.98]"
                       data-testid="android-download-btn">
                      <Download className="w-4 h-4" /> Download Android APK
                    </a>
                    {/* iter843 — Windows Desktop app download. The .exe
                        is uploaded to Emergent static hosting by the
                        team after each Electron build; the direct file
                        name is versioned via electron-builder's
                        artifactName ("SmartoERP-Setup-1.0.0.exe"). We
                        link to a stable "latest" alias served from
                        /downloads so this URL never has to change. */}
                    <a href="/downloads/SmartoERP-Setup-latest.exe" download
                       className="inline-flex items-center gap-2.5 bg-[#0369A1] hover:bg-[#0C4A6E] text-white px-7 py-3.5 rounded-full text-[15px] font-semibold transition-all shadow-xl shadow-[#0369A1]/25 hover:shadow-2xl hover:scale-[1.03] active:scale-[0.98]"
                       data-testid="windows-download-btn"
                       title="Native Windows POS with real USB thermal printer support">
                      <Monitor className="w-4 h-4" /> Download for Windows
                    </a>
                    {/* iter883 — Fallback for admins: source zip to build the .exe themselves
                        when the pre-built installer isn't uploaded yet. */}
                    <a href="/downloads/smarto-desktop-src.zip" download
                       className="inline-flex items-center gap-1.5 text-[12px] text-[#0369A1] hover:text-[#0C4A6E] underline underline-offset-2"
                       data-testid="windows-src-download-btn"
                       title="Electron source zip — build the .exe on any Windows PC (see README-BUILD-WINDOWS.md inside)">
                      Or build from source (admins)
                    </a>
                    <div className="flex items-center gap-3 bg-white rounded-2xl border border-gray-100 shadow-sm px-3 py-2.5">
                      <img src="/qr-app.png" alt="Scan to install Smarto" className="w-12 h-12 rounded-lg" data-testid="android-qr-code" />
                      <div className="leading-tight">
                        <p className="text-[12px] font-semibold text-[#1D1D1F]">Scan to install</p>
                        <p className="text-[11px] text-[#86868B]">Point your camera here</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right — device mockup */}
                <Reveal delay={0.15}>
                  <div className="relative flex justify-center">
                    {/* glow */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-[#047857]/15 to-[#10B981]/10 blur-3xl rounded-full" aria-hidden />
                    {/* phone frame */}
                    <div className="relative w-[270px] sm:w-[300px] rounded-[2.75rem] bg-[#1D1D1F] p-3 shadow-[0_30px_80px_rgb(0,0,0,0.25)] ring-1 ring-black/10">
                      <div className="absolute left-1/2 -translate-x-1/2 top-3 w-24 h-5 bg-[#1D1D1F] rounded-b-2xl z-10" aria-hidden />
                      <div className="rounded-[2.1rem] overflow-hidden bg-white">
                        <img src="/demo/pos.jpg" alt="Smarto POS running on Android" className="w-full" loading="lazy" />
                      </div>
                    </div>
                    {/* floating offline badge */}
                    <div className="absolute -left-2 sm:left-2 top-12 bg-white rounded-2xl shadow-xl border border-gray-100 px-3.5 py-2.5 flex items-center gap-2 animate-[float_4s_ease-in-out_infinite]">
                      <span className="w-7 h-7 rounded-lg bg-[#047857]/10 flex items-center justify-center"><WifiOff className="w-3.5 h-3.5 text-[#047857]" /></span>
                      <div className="leading-tight"><p className="text-[11px] font-bold text-[#1D1D1F]">Offline Mode</p><p className="text-[9.5px] text-[#86868B]">Sales never stop</p></div>
                    </div>
                    {/* floating printer badge */}
                    <div className="absolute -right-1 sm:right-2 bottom-14 bg-white rounded-2xl shadow-xl border border-gray-100 px-3.5 py-2.5 flex items-center gap-2 animate-[float_4s_ease-in-out_infinite_0.8s]">
                      <span className="w-7 h-7 rounded-lg bg-[#047857]/10 flex items-center justify-center"><Printer className="w-3.5 h-3.5 text-[#047857]" /></span>
                      <div className="leading-tight"><p className="text-[11px] font-bold text-[#1D1D1F]">Receipt printed</p><p className="text-[9.5px] text-[#86868B]">Thermal · BT · USB</p></div>
                    </div>
                  </div>
                </Reveal>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════ INDUSTRIES ═══════ */}
      <section id="industries" className="py-24 md:py-32 bg-[#F5F5F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#047857] mb-3">Industry-Specific</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight" style={heading}>
                Built For Your Industry.
              </h2>
              <p className="text-lg text-[#86868B] mt-4 max-w-xl mx-auto">Smarto adapts to your business with specialized POS configurations and workflows.</p>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.name} delay={i * 0.06}>
                <div className="relative rounded-2xl overflow-hidden group cursor-default h-48 md:h-56 shadow-sm hover:shadow-[0_20px_60px_rgb(0,0,0,0.15)] transition-all duration-500" data-testid={`industry-${ind.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className={`absolute inset-0 bg-gradient-to-br ${ind.gradient} group-hover:scale-105 transition-transform duration-700`} />
                  {/* Subtle pattern overlay for visual richness */}
                  <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.4) 0%, transparent 45%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.25) 0%, transparent 50%)' }} />
                  {/* Oversized icon silhouette — gives each tile a unique, branded "photo" feel */}
                  <ind.icon className="absolute -right-4 -top-4 w-40 h-40 text-white/15" strokeWidth={1.25} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-lg bg-white/20 backdrop-blur-md flex items-center justify-center">
                        <ind.icon className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-[14px] font-semibold text-white">{ind.name}</span>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ WHY SMARTO — COMPARISON ═══════ */}
      <ComparisonTable />

      {/* ═══════ ROI CALCULATOR ═══════ */}
      <ROICalculator onCTA={() => navigate('/register')} />

      {/* ═══════ PRICING ═══════ */}
      <section id="pricing" className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#047857] mb-3">Simple Pricing</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight" style={heading}>
                Start Free. Scale When Ready.
              </h2>
              <p className="text-lg text-[#86868B] mt-4">No hidden fees. 14-day free trial on every plan.</p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {PRICING.map((plan, i) => (
              <Reveal key={plan.name} delay={i * 0.08}>
                <div className={`rounded-2xl p-8 h-full flex flex-col transition-all duration-500 ${
                  plan.popular ? 'bg-white ring-2 ring-[#1D1D1F] shadow-[0_20px_60px_rgb(0,0,0,0.10)] relative scale-[1.02]' : 'bg-white border border-gray-100 hover:shadow-[0_12px_40px_rgb(0,0,0,0.06)]'
                }`} data-testid={`pricing-${plan.name.toLowerCase()}`}>
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#1D1D1F] text-white text-[11px] font-bold px-5 py-1 rounded-full shadow-sm">Most Popular</div>
                  )}
                  <h3 className="text-lg font-semibold" style={heading}>{plan.name}</h3>
                  <div className="mt-4 flex items-baseline gap-1">
                    {plan.price !== 'Custom' && <span className="text-[13px] text-[#86868B]">SR</span>}
                    <span className="text-4xl font-semibold tracking-tight" style={heading}>{plan.price}</span>
                    {plan.price !== 'Custom' && <span className="text-[13px] text-[#86868B]">/mo</span>}
                  </div>
                  <ul className="mt-8 space-y-3 flex-1">
                    {plan.features.map(f => (
                      <li key={f} className="flex items-center gap-2.5 text-[14px] text-[#86868B]">
                        <Check className="w-4 h-4 text-[#047857] shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => navigate(plan.price === 'Custom' ? '/login' : '/register')}
                    className={`w-full mt-8 py-3 rounded-full text-[14px] font-semibold transition-all active:scale-[0.98] ${
                      plan.popular
                        ? 'bg-gradient-to-r from-[#065F46] to-[#10B981] hover:from-[#065F46] hover:to-[#047857] text-white'
                        : 'bg-[#F5F5F7] hover:bg-gray-200 text-[#1D1D1F]'
                    }`} data-testid={`pricing-${plan.name.toLowerCase()}-btn`}>
                    {plan.price === 'Custom' ? 'Contact Sales' : 'Start Free Trial'}
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ TESTIMONIALS ═══════ */}
      <section className="py-24 md:py-32 bg-[#FAFAFA]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#047857] mb-3">What People Say</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight" style={heading}>
                Trusted By Businesses Worldwide.
              </h2>
              <p className="text-lg text-[#86868B] mt-4 max-w-xl mx-auto">See why growing companies choose Smarto for their operations.</p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="testimonials-section">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.1}>
                <div className="bg-white rounded-2xl p-7 border border-gray-100 shadow-sm hover:shadow-[0_20px_60px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-500 h-full flex flex-col" data-testid={`testimonial-${i}`}>
                  <div className="flex items-center gap-0.5 mb-4">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <Star key={j} className="w-4 h-4 text-[#D97706] fill-[#D97706]" />
                    ))}
                  </div>
                  <p className="text-[14px] text-[#1D1D1F]/80 leading-relaxed flex-1 italic">"{t.text}"</p>
                  <div className="flex items-center gap-3 mt-6 pt-5 border-t border-gray-100">
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${t.gradient} flex items-center justify-center text-white text-[13px] font-bold border-2 border-white shadow-sm shrink-0`} aria-label={t.name}>
                      {t.name.split(' ').map(p => p[0]).slice(0, 2).join('')}
                    </div>
                    <div>
                      <p className="text-[13px] font-semibold text-[#1D1D1F]">{t.name}</p>
                      <p className="text-[11px] text-[#86868B]">{t.role}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ TRUST & SECURITY ═══════ */}
      <section className="py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-12">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#047857] mb-3">Enterprise-Grade</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight" style={heading}>
                Your Data, Fully Protected.
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5" data-testid="trust-badges">
            {[
              { icon: Shield, label: 'AES-256 Encryption', desc: 'End-to-end data protection', color: 'from-emerald-500 to-teal-600' },
              { icon: Lock, label: 'Role-Based Access', desc: 'Granular permissions', color: 'from-blue-500 to-indigo-600' },
              { icon: Clock, label: '99.9% Uptime', desc: 'Enterprise reliability', color: 'from-violet-500 to-purple-600' },
              { icon: Award, label: 'Daily Backups', desc: 'Automated & redundant', color: 'from-[#047857] to-[#10B981]' },
            ].map((badge, i) => (
              <Reveal key={badge.label} delay={i * 0.08}>
                <div className="text-center p-5 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-[0_12px_40px_rgb(0,0,0,0.06)] hover:-translate-y-0.5 transition-all duration-500" data-testid={`trust-badge-${i}`}>
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${badge.color} flex items-center justify-center mx-auto mb-3 shadow-sm`}>
                    <badge.icon className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-[14px] font-semibold text-[#1D1D1F]" style={heading}>{badge.label}</p>
                  <p className="text-[12px] text-[#86868B] mt-1">{badge.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ FAQ ═══════ */}
      <section className="py-24 md:py-32 bg-[#F5F5F7]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#047857] mb-3">FAQ</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight" style={heading}>
                Common Questions.
              </h2>
            </div>
          </Reveal>
          <div className="space-y-3" data-testid="faq-section">
            {FAQS.map((faq, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm" data-testid={`faq-${i}`}>
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50/50 transition-colors"
                    data-testid={`faq-toggle-${i}`}>
                    <span className="text-[14px] font-semibold text-[#1D1D1F] pr-4">{faq.q}</span>
                    <motion.div animate={{ rotate: openFaq === i ? 180 : 0 }} transition={{ duration: 0.2 }}>
                      <ChevronDown className="w-4 h-4 text-[#86868B] shrink-0" />
                    </motion.div>
                  </button>
                  <AnimatePresence>
                    {openFaq === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}>
                        <div className="px-6 pb-4">
                          <p className="text-[13px] text-[#86868B] leading-relaxed">{faq.a}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ INTEGRATION PARTNERS ═══════ */}
      <section className="py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-12">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#047857] mb-3">Integrations</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight" style={heading}>
                Connects With Your Stack.
              </h2>
              <p className="text-lg text-[#86868B] mt-4 max-w-xl mx-auto">Smarto works with the tools your team already uses.</p>
            </div>
          </Reveal>
          <div className="flex flex-wrap items-center justify-center gap-4" data-testid="integrations-section">
            {INTEGRATIONS.map((name, i) => (
              <Reveal key={name} delay={i * 0.04}>
                <div className="flex items-center gap-2 px-5 py-3 bg-white rounded-full border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 cursor-default" data-testid={`integration-${name.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="w-6 h-6 rounded-md bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                    <span className="text-[10px] font-bold text-[#1D1D1F]">{name.charAt(0)}</span>
                  </div>
                  <span className="text-[13px] font-medium text-[#1D1D1F]">{name}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ SOCIAL MEDIA FEED ═══════ */}
      <section className="py-24 md:py-32 bg-[#FAFAFA]" data-testid="social-feed-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#047857] mb-3">Stay Connected</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight" style={heading}>
                Follow the Journey.
              </h2>
              <p className="text-lg text-[#86868B] mt-4 max-w-xl mx-auto">Product updates, tips, and behind-the-scenes — refreshed daily.</p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {dailyPosts.map((post, i) => (
              <Reveal key={post.platform + i} delay={i * 0.1}>
                {post.platform === 'facebook' && (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-[0_20px_60px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-500 h-full flex flex-col" data-testid={`social-post-facebook`}>
                    <div className="flex items-center gap-3 px-5 pt-5 pb-3">
                      <div className="w-10 h-10 rounded-full bg-[#1877F2] flex items-center justify-center shrink-0">
                        <span className="text-white font-black text-[15px]">f</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-[13px] font-semibold text-[#1D1D1F]">{post.handle}</p>
                        <p className="text-[10px] text-[#86868B]">Today at {new Date().getHours() > 12 ? new Date().getHours() - 12 : new Date().getHours()}:{String(new Date().getMinutes()).padStart(2, '0')} {new Date().getHours() >= 12 ? 'PM' : 'AM'}</p>
                      </div>
                      <span className="text-[9px] font-bold text-[#1877F2] bg-info/10 px-2 py-0.5 rounded-full">{post.tag}</span>
                    </div>
                    <p className="text-[13px] text-[#1D1D1F]/80 leading-relaxed px-5 pb-4 flex-1">{post.content}</p>
                    <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 text-[11px] text-[#86868B]">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5 text-[#1877F2]" /> {post.likes}</span>
                        <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> {post.comments}</span>
                      </div>
                      <span className="flex items-center gap-1">{post.shares} shares</span>
                    </div>
                  </div>
                )}
                {post.platform === 'instagram' && (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-[0_20px_60px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-500 h-full flex flex-col" data-testid={`social-post-instagram`}>
                    <div className="flex items-center gap-3 px-5 pt-5 pb-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-[#D97706] flex items-center justify-center shrink-0">
                        <span className="text-white font-black text-[14px]">ig</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-[13px] font-semibold text-[#1D1D1F]">@{post.handle}</p>
                        <p className="text-[10px] text-[#86868B]">Shared just now</p>
                      </div>
                      <span className="text-[9px] font-bold text-pink-500 bg-pink-50 px-2 py-0.5 rounded-full">{post.tag}</span>
                    </div>
                    <div className={`h-36 bg-gradient-to-br ${post.gradient} mx-5 rounded-xl mb-3 flex items-center justify-center`}>
                      <span className="text-white/80 text-[11px] font-semibold tracking-wider uppercase">Visual Content</span>
                    </div>
                    <p className="text-[13px] text-[#1D1D1F]/80 leading-relaxed px-5 pb-4 flex-1">{post.content}</p>
                    <div className="flex items-center gap-4 px-5 py-3 border-t border-gray-100 text-[11px] text-[#86868B]">
                      <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5 text-pink-500" /> {post.likes}</span>
                      <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> {post.comments}</span>
                    </div>
                  </div>
                )}
                {post.platform === 'twitter' && (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-[0_20px_60px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-500 h-full flex flex-col" data-testid={`social-post-twitter`}>
                    <div className="flex items-center gap-3 px-5 pt-5 pb-3">
                      <div className="w-10 h-10 rounded-full bg-[#0F1419] flex items-center justify-center shrink-0">
                        <span className="text-white font-black text-[14px]">X</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-[13px] font-semibold text-[#1D1D1F]">@{post.handle}</p>
                        <p className="text-[10px] text-[#86868B]">{Math.floor(Math.random() * 12) + 1}h ago</p>
                      </div>
                      <span className="text-[9px] font-bold text-[#0F1419] bg-gray-100 px-2 py-0.5 rounded-full">{post.tag}</span>
                    </div>
                    <p className="text-[14px] text-[#1D1D1F] leading-relaxed px-5 pb-4 flex-1">{post.content}</p>
                    <div className="flex items-center gap-5 px-5 py-3 border-t border-gray-100 text-[11px] text-[#86868B]">
                      <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5" /> {post.likes}</span>
                      <span className="flex items-center gap-1">&#x21BB; {post.retweets}</span>
                    </div>
                  </div>
                )}
              </Reveal>
            ))}
          </div>

          {/* Social Profile Links */}
          <Reveal delay={0.35}>
            <div className="flex items-center justify-center gap-4 mt-12" data-testid="social-profile-links">
              {[
                { platform: 'Facebook', handle: 'SmartoERP', url: SOCIAL_LINKS.facebook, color: 'bg-[#1877F2]', letter: 'f' },
                { platform: 'Instagram', handle: '@smarto.erp', url: SOCIAL_LINKS.instagram, color: 'bg-gradient-to-br from-purple-500 via-pink-500 to-[#D97706]', letter: 'ig' },
                { platform: 'X (Twitter)', handle: '@SmartoERP', url: SOCIAL_LINKS.twitter, color: 'bg-[#0F1419]', letter: 'X' },
              ].map(s => (
                <a key={s.platform} href={s.url} target="_blank" rel="noopener noreferrer"
                  className="group flex items-center gap-3 px-5 py-3 bg-white rounded-full border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
                  data-testid={`social-link-${s.platform.toLowerCase().replace(/[^a-z]/g, '')}`}>
                  <div className={`w-8 h-8 rounded-full ${s.color} flex items-center justify-center`}>
                    <span className="text-white font-black text-[11px]">{s.letter}</span>
                  </div>
                  <div>
                    <p className="text-[12px] font-semibold text-[#1D1D1F] group-hover:text-[#065F46] transition-colors">{s.platform}</p>
                    <p className="text-[10px] text-[#86868B]">{s.handle}</p>
                  </div>
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════ LEAD CAPTURE FORM ═══════ */}
      <section className="py-24 md:py-32" data-testid="lead-capture-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left - Copy */}
            <Reveal>
              <div>
                <div className="inline-flex items-center gap-2 bg-[#ECFDF5] rounded-full px-4 py-1.5 mb-5">
                  <Sparkles className="w-3.5 h-3.5 text-[#047857]" />
                  <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#065F46]">Personalized Demo</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-semibold tracking-tight" style={heading}>
                  Get a Demo Tailored<br />to Your Business.
                </h2>
                <p className="text-[15px] text-[#86868B] mt-4 leading-relaxed">
                  Tell us about your business and we'll prepare a personalized walkthrough showing exactly how Smarto fits your workflow.
                </p>
                <div className="mt-8 space-y-4">
                  {[
                    { icon: Clock, text: 'Hear back within 24 hours' },
                    { icon: Users, text: 'Speak with a product specialist' },
                    { icon: Shield, text: 'No obligation, completely free' },
                  ].map(item => (
                    <div key={item.text} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#F5F5F7] flex items-center justify-center shrink-0">
                        <item.icon className="w-4 h-4 text-[#1D1D1F]" />
                      </div>
                      <span className="text-[13px] text-[#1D1D1F] font-medium">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            {/* Right - Form */}
            <Reveal delay={0.15}>
              <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_20px_60px_rgb(0,0,0,0.06)] p-8" data-testid="lead-form-card">
                <AnimatePresence mode="wait">
                  {leadSubmitted ? (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center py-8"
                      data-testid="lead-form-success">
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.1, type: 'spring', stiffness: 300 }}
                        className="w-16 h-16 rounded-2xl bg-success flex items-center justify-center mx-auto mb-5 shadow-lg shadow-emerald-200">
                        <Check className="w-7 h-7 text-white" />
                      </motion.div>
                      <h3 className="text-xl font-bold text-[#1D1D1F]" style={heading}>You're All Set!</h3>
                      <p className="text-[13px] text-[#86868B] mt-2 max-w-xs mx-auto">
                        We'll prepare your personalized demo and reach out within 24 hours.
                      </p>
                      <button onClick={() => setTourOpen(true)}
                        className="mt-6 inline-flex items-center gap-2 bg-[#F5F5F7] hover:bg-gray-200 text-[#1D1D1F] px-6 py-2.5 rounded-full text-[13px] font-semibold transition-colors"
                        data-testid="lead-form-try-demo-btn">
                        <Play className="w-3.5 h-3.5" /> Try the Live Demo Now
                      </button>
                    </motion.div>
                  ) : (
                    <motion.form
                      key="form"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onSubmit={handleLeadSubmit}
                      className="space-y-4"
                      data-testid="lead-capture-form">
                      <div>
                        <label className="text-[11px] font-semibold text-[#86868B] uppercase tracking-wider">Full Name *</label>
                        <input type="text" required
                          value={leadForm.name}
                          onChange={e => setLeadForm(p => ({ ...p, name: e.target.value }))}
                          placeholder="John Smith"
                          className="mt-1 w-full h-11 px-4 rounded-xl border border-gray-200 text-[14px] text-[#1D1D1F] placeholder-gray-300 focus:border-[#7FB0FF] focus:ring-2 focus:ring-[#DCE8FF] outline-none transition-all"
                          data-testid="lead-input-name" />
                      </div>
                      <div>
                        <label className="text-[11px] font-semibold text-[#86868B] uppercase tracking-wider">Work Email *</label>
                        <input type="email" required
                          value={leadForm.email}
                          onChange={e => setLeadForm(p => ({ ...p, email: e.target.value }))}
                          placeholder="john@company.com"
                          className="mt-1 w-full h-11 px-4 rounded-xl border border-gray-200 text-[14px] text-[#1D1D1F] placeholder-gray-300 focus:border-[#7FB0FF] focus:ring-2 focus:ring-[#DCE8FF] outline-none transition-all"
                          data-testid="lead-input-email" />
                      </div>
                      <div>
                        <label className="text-[11px] font-semibold text-[#86868B] uppercase tracking-wider">Phone Number</label>
                        <input type="tel"
                          value={leadForm.phone}
                          onChange={e => setLeadForm(p => ({ ...p, phone: e.target.value }))}
                          placeholder="+966 5xx xxx xxxx"
                          className="mt-1 w-full h-11 px-4 rounded-xl border border-gray-200 text-[14px] text-[#1D1D1F] placeholder-gray-300 focus:border-[#7FB0FF] focus:ring-2 focus:ring-[#DCE8FF] outline-none transition-all"
                          data-testid="lead-input-phone" />
                      </div>
                      <div>
                        <label className="text-[11px] font-semibold text-[#86868B] uppercase tracking-wider">Industry</label>
                        <select
                          value={leadForm.industry}
                          onChange={e => setLeadForm(p => ({ ...p, industry: e.target.value }))}
                          className="mt-1 w-full h-11 px-4 rounded-xl border border-gray-200 text-[14px] text-[#1D1D1F] focus:border-[#7FB0FF] focus:ring-2 focus:ring-[#DCE8FF] outline-none transition-all bg-white appearance-none cursor-pointer"
                          data-testid="lead-input-industry">
                          <option value="">Select your industry</option>
                          {industryOptions}
                        </select>
                      </div>
                      <div>
                        <label className="text-[11px] font-semibold text-[#86868B] uppercase tracking-wider">Message <span className="text-gray-300">(optional)</span></label>
                        <textarea
                          value={leadForm.message}
                          onChange={e => setLeadForm(p => ({ ...p, message: e.target.value }))}
                          placeholder="Tell us about your business needs..."
                          rows={3}
                          className="mt-1 w-full px-4 py-3 rounded-xl border border-gray-200 text-[14px] text-[#1D1D1F] placeholder-gray-300 focus:border-[#7FB0FF] focus:ring-2 focus:ring-[#DCE8FF] outline-none transition-all resize-none"
                          data-testid="lead-input-message" />
                      </div>
                      {leadError && (
                        <p className="text-[12px] text-error font-medium" data-testid="lead-form-error">{leadError}</p>
                      )}
                      <button type="submit" disabled={leadSubmitting}
                        className="w-full bg-[#1D1D1F] hover:bg-black text-white py-3.5 rounded-full text-[14px] font-semibold transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm"
                        data-testid="lead-submit-btn">
                        <Send className="w-4 h-4" /> {leadSubmitting ? 'Sending...' : 'Request Personalized Demo'}
                      </button>
                      <p className="text-[10px] text-[#86868B] text-center">We respect your privacy. No spam, ever.</p>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════ CTA ═══════ */}
      <section className="py-24 md:py-32 bg-[#0B1120] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[46rem] h-[46rem] bg-[#047857]/15 blur-[130px] rounded-full" aria-hidden />
        <Reveal>
          <div className="max-w-3xl mx-auto text-center px-6 relative z-10">
            <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#D97706] mb-4">Start today</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white" style={headingWhite}>
              Ready to unify your business operations?
            </h2>
            <p className="text-[#94A3B8] mt-4 text-lg">Join thousands of businesses worldwide that trust Smarto for their daily operations.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-10">
              <button onClick={() => navigate('/register')}
                className="bg-[#047857] hover:bg-[#065F46] text-white px-10 py-4 rounded-full text-[15px] font-semibold transition-all active:scale-[0.98] shadow-xl shadow-[#047857]/30 hover:shadow-2xl hover:scale-[1.02]" data-testid="cta-start-btn">
                Start Your Free Trial
              </button>
              <button onClick={() => navigate('/login')}
                className="text-[15px] font-semibold text-[#94A3B8] hover:text-white transition-colors px-6 py-4" data-testid="cta-login-btn">
                Sign in
              </button>
            </div>
            <p className="text-[13px] text-[#94A3B8]/60 mt-6">
              Or reach us at <a href="mailto:info@smartoerp.com" className="text-[#D97706] hover:underline">info@smartoerp.com</a>
            </p>
          </div>
        </Reveal>
      </section>

      {/* ═══════ FOOTER ═══════ */}
      <footer className="py-16 md:py-20 pb-24 md:pb-20 px-6 md:px-12 border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          {/* iter741 — Download section above the footer links so it's
              easy to discover. Two clear CTAs: APK for Android (direct
              install) and PWA-style "Add to Home Screen" for iOS. */}
          <div className="mb-12 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="footer-download-section">
            <a
              href="/api/apk/download"
              download
              className="group flex items-center gap-4 p-5 rounded-2xl border-2 border-gray-200 hover:border-[#D97706] hover:bg-[#ECFDF5]/40 transition-all"
              data-testid="footer-android-download"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-500/20">
                <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor"><path d="M7.6 15.5a1 1 0 11-2 0 1 1 0 012 0zm10.8 0a1 1 0 11-2 0 1 1 0 012 0zM6.4 7.3l1.5-2.6a.3.3 0 00-.5-.3l-1.5 2.6A8.2 8.2 0 002 13h20a8.2 8.2 0 00-3.9-6.1l1.5-2.5a.3.3 0 00-.5-.3l-1.5 2.6a9.3 9.3 0 00-7.2 0z"/></svg>
              </div>
              <div className="flex-1">
                <div className="text-xs font-semibold uppercase tracking-wider text-emerald-600 flex items-center gap-2">
                  Android <ApkBuildBadge />
                </div>
                <div className="text-base font-bold text-stone-900">Download APK</div>
                <div className="text-xs text-stone-500 mt-0.5">Direct install · no Play Store needed</div>
              </div>
              <Download className="w-5 h-5 text-stone-400 group-hover:text-[#065F46] group-hover:translate-y-0.5 transition-all" />
            </a>
            <button
              onClick={() => document.querySelector('[data-testid="install-app-banner"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
              className="group flex items-center gap-4 p-5 rounded-2xl border-2 border-gray-200 hover:border-[#D97706] hover:bg-[#ECFDF5]/40 transition-all text-left"
              data-testid="footer-ios-install"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-stone-800 to-stone-900 flex items-center justify-center flex-shrink-0 shadow-lg shadow-stone-900/20">
                <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor"><path d="M16.4 12.3a3.3 3.3 0 011.6-2.7 3.4 3.4 0 00-2.7-1.4c-1.1-.1-2.2.7-2.8.7-.6 0-1.5-.7-2.5-.6a3.6 3.6 0 00-3 1.8c-1.3 2.2-.3 5.5 1 7.3.6.9 1.4 1.9 2.4 1.8 1 0 1.3-.6 2.5-.6 1.2 0 1.5.6 2.5.6s1.7-.9 2.3-1.8a8 8 0 001-2.1 3.3 3.3 0 01-1.8-3zM14.6 6.5a3.2 3.2 0 00.7-2.3 3.2 3.2 0 00-2.1 1.1 3 3 0 00-.7 2.2c.8.1 1.6-.4 2.1-1z"/></svg>
              </div>
              <div className="flex-1">
                <div className="text-xs font-semibold uppercase tracking-wider text-stone-700">iPhone / iPad</div>
                <div className="text-base font-bold text-stone-900">Install on iOS</div>
                <div className="text-xs text-stone-500 mt-0.5">Safari → Share → Add to Home Screen</div>
              </div>
              <ChevronRight className="w-5 h-5 text-stone-400 group-hover:text-[#065F46] group-hover:translate-x-0.5 transition-all" />
            </button>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2.5">
              <img src="/smarto-icon.png" alt="" className="h-6 w-6 object-contain" />
              <span className="text-sm font-extrabold tracking-wider" style={heading}>SMARTO</span>
            </div>
            <div className="flex flex-wrap items-center justify-center md:justify-end gap-4 sm:gap-8 text-[13px] text-[#86868B]">
              <a href="#features" className="hover:text-[#1D1D1F] transition-colors">Features</a>
              <a href="#preview" className="hover:text-[#1D1D1F] transition-colors">Preview</a>
              <a href="#industries" className="hover:text-[#1D1D1F] transition-colors">Industries</a>
              <a href="#pricing" className="hover:text-[#1D1D1F] transition-colors">Pricing</a>
              <button onClick={() => navigate('/login')} className="hover:text-[#1D1D1F] transition-colors">Sign In</button>
            </div>
          </div>
          <div className="mt-10 pt-8 border-t border-gray-100 flex flex-col md:flex-row items-center justify-between gap-4 text-[12px] text-[#86868B]">
            <p>&copy; {new Date().getFullYear()} Smarto. All rights reserved.</p>
            <div className="flex items-center gap-3">
              {[
                { url: SOCIAL_LINKS.facebook, letter: 'f', color: 'bg-[#1877F2]' },
                { url: SOCIAL_LINKS.instagram, letter: 'ig', color: 'bg-gradient-to-br from-purple-500 via-pink-500 to-[#D97706]' },
                { url: SOCIAL_LINKS.twitter, letter: 'X', color: 'bg-[#0F1419]' },
              ].map(s => (
                <a key={s.letter} href={s.url} target="_blank" rel="noopener noreferrer"
                  className={`w-7 h-7 rounded-full ${s.color} flex items-center justify-center hover:opacity-80 transition-opacity`}
                  data-testid={`footer-social-${s.letter}`}>
                  <span className="text-white font-bold text-[9px]">{s.letter}</span>
                </a>
              ))}
            </div>
            <p className="flex items-center gap-1.5">
              <img src="/smarto-icon.png" alt="" className="h-3.5 w-3.5 object-contain opacity-50" />
              Powered by SMARTO
            </p>
          </div>
        </div>
      </footer>

      {/* ═══════ PRODUCT TOUR MODAL ═══════ */}
      <ProductTourModal isOpen={tourOpen} onClose={() => setTourOpen(false)} />

      {/* ═══════ EXIT-INTENT POPUP ═══════ */}
      <AnimatePresence>
        {exitPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
            onClick={() => setExitPopup(false)}
            data-testid="exit-intent-overlay">
            <motion.div
              initial={{ scale: 0.85, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.85, opacity: 0, y: 30 }}
              transition={{ type: 'spring', stiffness: 400, damping: 28 }}
              className="bg-white rounded-3xl shadow-[0_40px_100px_rgb(0,0,0,0.25)] max-w-md w-full overflow-hidden relative"
              onClick={e => e.stopPropagation()}
              data-testid="exit-intent-popup">
              {/* Top gradient bar */}
              <div className="h-1.5 bg-gradient-to-r from-[#047857] via-[#D97706] to-[#065F46]" />
              {/* Close button */}
              <button onClick={() => setExitPopup(false)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors z-10"
                data-testid="exit-intent-close-btn">
                <span className="text-[#86868B] text-lg leading-none">&times;</span>
              </button>
              {/* Content */}
              <div className="px-8 pt-8 pb-6 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.15, type: 'spring', stiffness: 300 }}
                  className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#047857] to-[#D97706] flex items-center justify-center mx-auto mb-5 shadow-lg shadow-[#BBD2FF]">
                  <Play className="w-7 h-7 text-white ml-0.5" />
                </motion.div>
                <h3 className="text-2xl font-bold text-[#1D1D1F] tracking-tight" style={heading}>
                  Before You Go...
                </h3>
                <p className="text-[15px] text-[#86868B] mt-3 leading-relaxed max-w-xs mx-auto">
                  Take a 2-minute interactive tour of Smarto. See every feature in action — no signup needed.
                </p>
                {/* Stats row */}
                <div className="flex items-center justify-center gap-6 mt-6 mb-6">
                  {[
                    { val: '11', label: 'Screens' },
                    { val: '2 min', label: 'Duration' },
                    { val: 'Free', label: 'Always' },
                  ].map(s => (
                    <div key={s.label} className="text-center">
                      <p className="text-lg font-bold text-[#1D1D1F]" style={heading}>{s.val}</p>
                      <p className="text-[10px] text-[#86868B] font-medium uppercase tracking-wider">{s.label}</p>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => { setExitPopup(false); setTourOpen(true); }}
                  className="w-full bg-[#1D1D1F] hover:bg-black text-white py-3.5 rounded-full text-[15px] font-semibold transition-all active:scale-[0.98] shadow-sm flex items-center justify-center gap-2"
                  data-testid="exit-intent-demo-btn">
                  <Play className="w-4 h-4" /> Launch Live Demo
                </button>
                <button
                  onClick={() => setExitPopup(false)}
                  className="mt-3 text-[13px] text-[#86868B] hover:text-[#1D1D1F] transition-colors font-medium"
                  data-testid="exit-intent-dismiss-btn">
                  No thanks, maybe later
                </button>
              </div>
              {/* Bottom trust line */}
              <div className="bg-[#F9F9F9] border-t border-gray-100 px-8 py-3 flex items-center justify-center gap-4 text-[11px] text-[#86868B]">
                {['No credit card', 'No signup', 'Instant access'].map(t => (
                  <span key={t} className="flex items-center gap-1"><Check className="w-3 h-3 text-success" />{t}</span>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══════ VISITOR ACTIVITY TICKER ═══════ */}
      <AnimatePresence>
        {tickerVisible && tickerItem && !tickerDismissed && (
          <motion.div
            initial={{ x: -100, opacity: 0, scale: 0.9 }}
            animate={{ x: 0, opacity: 1, scale: 1 }}
            exit={{ x: -100, opacity: 0, scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className="fixed bottom-20 sm:bottom-20 left-3 sm:left-5 z-50 max-w-[calc(100vw-24px)] sm:max-w-sm"
            data-testid="visitor-activity-ticker">
            <div className="bg-white rounded-2xl shadow-[0_20px_60px_rgb(0,0,0,0.15)] border border-gray-100 px-5 py-4 flex items-start gap-3.5">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                tickerItem.type === 'demo_start' ? 'bg-info' :
                tickerItem.type === 'demo_complete' ? 'bg-success' : 'bg-[#047857]'
              }`}>
                {tickerItem.type === 'demo_start' && <Play className="w-4.5 h-4.5 text-white" />}
                {tickerItem.type === 'demo_complete' && <Check className="w-4.5 h-4.5 text-white" />}
                {tickerItem.type === 'signup' && <Users className="w-4.5 h-4.5 text-white" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-[#1D1D1F] leading-snug">{tickerItem.message}</p>
                <div className="flex items-center gap-2 mt-1">
                  <MapPin className="w-3 h-3 text-[#86868B]" />
                  <span className="text-[11px] text-[#86868B]">{tickerItem.time_ago}</span>
                </div>
              </div>
              <button onClick={() => { setTickerDismissed(true); setTickerVisible(false); }}
                className="w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center shrink-0 mt-0.5 transition-colors"
                data-testid="ticker-dismiss-btn">
                <span className="text-[#86868B] text-xs leading-none">&times;</span>
              </button>
            </div>
            {/* Pulse dot */}
            <div className="absolute -top-1 -right-1 w-3 h-3">
              <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75" />
              <span className="absolute inset-0 rounded-full bg-success" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══════ STICKY CTA BAR ═══════ */}
      <AnimatePresence>
        {showStickyCta && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed bottom-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-xl border-t border-gray-100 shadow-[0_-4px_20px_rgb(0,0,0,0.06)]"
            data-testid="sticky-cta-bar">
            <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
              <div className="hidden sm:flex items-center gap-3">
                <img src="/smarto-icon.png" alt="" className="h-6 w-6 object-contain" />
                <span className="text-[13px] font-semibold text-[#1D1D1F]">Ready to streamline your business?</span>
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto justify-center sm:justify-end">
                <a href="/api/apk/download" download
                  className="hidden sm:flex items-center gap-1.5 text-[13px] font-semibold text-[#047857] hover:text-[#065F46] transition-colors px-3 py-2"
                  data-testid="sticky-download-app-btn">
                  <Smartphone className="w-3.5 h-3.5" /> Download App
                </a>
                <button onClick={() => setTourOpen(true)}
                  className="text-[13px] font-semibold text-[#86868B] hover:text-[#1D1D1F] transition-colors flex items-center gap-1.5 px-4 py-2"
                  data-testid="sticky-demo-btn">
                  <Play className="w-3.5 h-3.5" /> Live Demo
                </button>
                <button onClick={() => navigate('/register')}
                  className="bg-[#1D1D1F] hover:bg-black text-white px-6 py-2.5 rounded-full text-[13px] font-semibold transition-all active:scale-[0.98] shadow-sm"
                  data-testid="sticky-start-btn">
                  Start Free Trial <ArrowRight className="w-3.5 h-3.5 inline ml-1" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
